import { useCallback, useState } from "react";
import { toast } from "sonner";

interface AsyncActionOptions {
  loadingMessage?: string;
  successMessage?: string;
  errorMessage?: string;
  showLoadingToast?: boolean;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

export function useAsyncAction<T extends unknown[], R>(
  asyncFn: (...args: T) => Promise<R>,
  options: AsyncActionOptions = {}
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const {
    loadingMessage = "Processing...",
    successMessage = "Action completed successfully",
    errorMessage = "An error occurred",
    showLoadingToast = true,
    showSuccessToast = true,
    showErrorToast = true,
  } = options;

  const execute = useCallback(
    async (...args: T): Promise<R | null> => {
      setIsLoading(true);
      setError(null);

      let loadingToastId: string | number | undefined;

      try {
        // Show loading toast if enabled
        if (showLoadingToast) {
          loadingToastId = toast.loading(loadingMessage, {
            duration: Number.POSITIVE_INFINITY, // Don't auto-dismiss loading toasts
          });
        }

        const result = await asyncFn(...args);

        // Dismiss loading toast
        if (loadingToastId) {
          toast.dismiss(loadingToastId);
        }

        // Show success toast if enabled
        if (showSuccessToast) {
          toast.success(successMessage);
        }

        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);

        // Dismiss loading toast
        if (loadingToastId) {
          toast.dismiss(loadingToastId);
        }

        // Show error toast if enabled
        if (showErrorToast) {
          toast.error(error.message || errorMessage);
        }

        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [
      asyncFn,
      loadingMessage,
      successMessage,
      errorMessage,
      showLoadingToast,
      showSuccessToast,
      showErrorToast,
    ]
  );

  return {
    execute,
    isLoading,
    error,
    reset: () => {
      setError(null);
      setIsLoading(false);
    },
  };
}
